import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Shield, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import CommandLayout from "@/components/CommandLayout";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import ScriptFactoryModal from "@/components/ScriptFactoryModal";
import type { RehearsalData } from "@/components/ScriptFactoryModal";
import { routeAnalysis } from "@/lib/resultRouter";

interface AnalysisCase {
  id: string;
  raw_text: string;
  toxicity_score: number;
  manipulation_primary_type: string | null;
  grey_rock_response: string | null;
  created_at: string;
}

export default function ContentLibrary() {
  const { user } = useAuth();
  const location = useLocation();
  const navState = location.state as {
    openScriptFactory?: boolean;
    rehearsalData?: RehearsalData;
    initialCardType?: string;
  } | null;

  const [selectedCase, setSelectedCase] = useState<AnalysisCase | null>(null);
  const [rehearsalData, setRehearsalData] = useState<RehearsalData | null>(null);
  const [initialCardType, setInitialCardType] = useState<string | undefined>(undefined);

  const { data: cases = [], isLoading } = useQuery({
    queryKey: ["content-cases"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("neutralizations")
        .select("id, raw_text, toxicity_score, manipulation_primary_type, grey_rock_response, created_at")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as AnalysisCase[];
    },
    enabled: !!user,
  });

  // Auto-open Script Factory when navigating from debrief (one-shot)
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (navState?.openScriptFactory && cases.length > 0 && !autoOpened) {
      setAutoOpened(true);
      setSelectedCase(cases[0]);
      if (navState.rehearsalData) setRehearsalData(navState.rehearsalData);
      if (navState.initialCardType) setInitialCardType(navState.initialCardType);
      window.history.replaceState({}, document.title);
    }
  }, [navState, cases, autoOpened]);

  if (!user) {
    return (
      <CommandLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground">Please log in to access Content.</p>
        </div>
      </CommandLayout>
    );
  }

  return (
    <CommandLayout>
      <PageBreadcrumb items={[{ label: "Dashboard", path: "/dashboard" }, { label: "Content Hub" }]} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">
          <span className="text-foreground">Content </span>
          <span className="text-gradient-cyan">Hub</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Select an analysis to generate scripts & share cards for social media
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Loading analyses...</div>
      ) : cases.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <Shield className="w-10 h-10 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground">No analyses yet. Analyze a message first to create content.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {cases.map((c) => {
            const analysis = routeAnalysis(c.raw_text);
            const isHot = analysis.score >= 8;
            return (
              <button
                key={c.id}
                onClick={() => setSelectedCase(c)}
                className={cn(
                  "w-full flex items-center gap-3 p-4 rounded-xl border text-left transition-all hover:scale-[1.01]",
                  isHot
                    ? "bg-red-500/5 border-red-500/20 hover:border-red-500/40"
                    : "bg-card border-border hover:border-cyan/30"
                )}
              >
                <span
                  className={cn(
                    "shrink-0 text-xs font-mono font-bold px-2 py-1 rounded",
                    isHot ? "bg-red-500/20 text-red-400" : "bg-cyan/10 text-cyan"
                  )}
                >
                  {analysis.score.toFixed(1)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground line-clamp-2">{c.raw_text}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground">
                      {analysis.verdict}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(c.created_at).toLocaleDateString()}
                    </span>
                    {c.grey_rock_response && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-semibold">
                        Neutralized
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      <ScriptFactoryModal
        open={!!selectedCase}
        onClose={() => {
          setSelectedCase(null);
          setRehearsalData(null);
          setInitialCardType(undefined);
        }}
        item={selectedCase}
        rehearsalData={rehearsalData}
        initialCardType={initialCardType as any}
      />
    </CommandLayout>
  );
}
